import React, { createContext, useEffect, useState } from 'react';
import { signMessage } from '@wagmi/core';

import userService from 'services/user';

import wagmiConfig from 'wagmiConfig';
import { AUTH_MESSAGE_TO_SIGN, SIGNATURE_EXPIRY_TIME } from 'constants/index';
import useWalletContext from 'hooks/useWalletContext';
import useNotification from 'hooks/useNotification';

export const TokenContext = createContext(null);

const WALLET_TOKENS_KEY = 'wallet_tokens';
const ACTIVE_WALLET_KEY = 'active_wallet_address';

const isBrowser = () => typeof window !== 'undefined';

const readWalletTokens = () => {
  if (!isBrowser()) {
    return {};
  }

  const raw = localStorage.getItem(WALLET_TOKENS_KEY);

  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch (err) {
    console.warn('Failed to parse wallet tokens store', err);
    return {};
  }
};

const writeWalletTokens = (tokensMap) => {
  if (!isBrowser()) {
    return;
  }

  const keys = Object.keys(tokensMap || {});
  if (!keys.length) {
    localStorage.removeItem(WALLET_TOKENS_KEY);
    return;
  }

  localStorage.setItem(WALLET_TOKENS_KEY, JSON.stringify(tokensMap));
};

const getWalletTokenEntry = (address) => {
  if (!address) {
    return null;
  }

  const tokens = readWalletTokens();
  return tokens[address.toLowerCase()] || null;
};

const setActiveWallet = (address) => {
  if (!isBrowser()) {
    return;
  }

  if (!address) {
    localStorage.removeItem(ACTIVE_WALLET_KEY);
    return;
  }

  localStorage.setItem(ACTIVE_WALLET_KEY, address.toLowerCase());
};

const upsertWalletTokenEntry = (address, token, expiresAt) => {
  if (!isBrowser() || !address || !token || !expiresAt) {
    return;
  }

  const lowerAddress = address.toLowerCase();
  const tokens = readWalletTokens();

  tokens[lowerAddress] = {
    token,
    expiresAt,
    lastUsed: Date.now(),
  };

  writeWalletTokens(tokens);
  setActiveWallet(lowerAddress);
};

const removeWalletTokenEntry = (address) => {
  if (!isBrowser() || !address) {
    return;
  }

  const lowerAddress = address.toLowerCase();
  const tokens = readWalletTokens();

  if (!tokens[lowerAddress]) {
    return;
  }

  delete tokens[lowerAddress];
  writeWalletTokens(tokens);

  if (localStorage.getItem(ACTIVE_WALLET_KEY) === lowerAddress) {
    clearActiveWallet();
  }
};

const clearActiveWallet = () => {
  setActiveWallet(null);
};

const clearAuthStorage = () => {
  if (!isBrowser()) {
    return;
  }

  localStorage.removeItem('token');
};

const getInitialAuthState = () => {
  try {
    const activeAddress = localStorage.getItem(ACTIVE_WALLET_KEY);
    if (!activeAddress) {
      return false;
    }
    const entry = getWalletTokenEntry(activeAddress);
    return !!(entry && entry.token && entry.expiresAt > Date.now());
  } catch {
    return false;
  }
};

export const TokenProvider = ({ children }) => {
  const { disconnect, address, isConnected } = useWalletContext();
  const showNotification = useNotification();

  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setAuthenticated] = useState(getInitialAuthState);

  const signOut = async (isTokenInvalid = false) => {
    setLoading(true);
    try {
      if (!isTokenInvalid && localStorage.token) {
        await userService.logout();
      }
    } catch (err) {
      // Logout API failure should not block local cleanup
      console.warn('Logout API call failed', err);
    } finally {
      // Always clear local auth state regardless of API success/failure
      clearAuthStorage();

      // Only remove the stored wallet token entry if it's explicitly invalid (401)
      // On normal sign-out, preserve it for 7-day re-authentication
      if (isTokenInvalid) {
        const targetAddress = address || localStorage.getItem(ACTIVE_WALLET_KEY);
        if (targetAddress) {
          removeWalletTokenEntry(targetAddress);
        }
      }

      // Set auth state directly rather than relying on disconnect() → useEffect chain,
      // which is fragile if isConnected doesn't change (e.g. already disconnected)
      setAuthenticated(false);
      disconnect();
      setLoading(false);
    }
  };

  const signIn = async () => {
    setLoading(true);
    try {
      const nonceData = await userService.getNonce(address);

      if (nonceData?.message === 'Account locked!') {
        return showNotification({ type: 'error', message: nonceData?.message });
      }

      const signResult = await signMessage(wagmiConfig, {
        message: AUTH_MESSAGE_TO_SIGN(address, nonceData.data.nonce),
      });

      const authResponseData = await userService.authenticate(address, signResult);
      if (authResponseData) {
        const { token } = authResponseData.data.auth;
        localStorage.setItem('token', token);
        const expiresAt = Date.now() + SIGNATURE_EXPIRY_TIME;
        upsertWalletTokenEntry(address, token, expiresAt);

        setAuthenticated(true);
        showNotification({ type: 'success', message: 'Wallet connected successfully' });
      }
    } catch (error) {
      showNotification({ type: 'error', message: 'Failed to connect wallet' });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const normalizedAddress = address?.toLowerCase();

    // When wallet disconnects, clear current session but keep stored tokens for multi-wallet support
    if (!isConnected || !normalizedAddress) {
      clearAuthStorage();
      setAuthenticated(false);
      return;
    }

    const entry = getWalletTokenEntry(normalizedAddress);

    if (entry && entry.token && entry.expiresAt > Date.now()) {
      // Sync localStorage.token from wallet_tokens so ApiClient always has the latest
      if (localStorage.getItem('token') !== entry.token) {
        localStorage.setItem('token', entry.token);
      }
      // Keep active_wallet_address current with the actual connected wallet,
      // not just the last wallet that signed in (which could be stale after a switch)
      setActiveWallet(normalizedAddress);
      setAuthenticated(true);
      return;
    }

    if (entry && entry.expiresAt <= Date.now()) {
      removeWalletTokenEntry(normalizedAddress);
    }

    clearAuthStorage();
    setAuthenticated(false);
  }, [address, isConnected]);

  return (
    <TokenContext.Provider
      value={{
        isAuthenticated,
        connectedAndAuth: isConnected && isAuthenticated,
        loading,
        signIn,
        signOut,
      }}
    >
      {children}
    </TokenContext.Provider>
  );
};
